import React from 'react'

import {intersection, sumBy} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import invariant from 'invariant'

import config from '../../config'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {signOut} from '../../redux/features/auth'

import useSearch from '../../../../commons/src/hooks/use-search'
import {HttpStatusError} from '../../../../commons/src/libs/http-json'
import {
  getEndOfDay,
  getFirstDayOfCurrentMonth,
  getStartOfDay,
  getTodaysDate,
  isBefore,
} from '../../../../commons/src/services/date'
import {captureException, captureIgnoredException} from '../../../../commons/src/services/logging'
import useSortAttributes, {SortDefinition} from '../../hooks/use-sort-attributes'
import useTableRowFilters, {FilterMap} from '../../hooks/use-table-row-filters'
import {formatDate, formatPrice} from '../../libs/formatter'
import {transformInternalCreditNote, transformInternalOrderInvoice} from '../../libs/order'
import {BooleanMatchingFilter, filterTableRows} from '../../libs/table-row-filter'
import * as internalApi from '../../services/api/internal'
import * as partnerApi from '../../services/api/partner'
import {createInvoiceSearch} from '../../services/search'

import {DateRange, DateRangeConfig} from '../../../../commons/src/types'

import refreshIcon from '../../../../commons/src/assets/icons/refresh.svg'

import ActionBar from '../../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../../commons/src/components/button'
import FilterBar from '../../../../commons/src/components/filter-bar'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon/icon'
import Link from '../../../../commons/src/components/link'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator'
import Paragraph from '../../../../commons/src/components/paragraph'
import SearchInputField from '../../../../commons/src/components/search-input-field'
import SplitLayout from '../../../../commons/src/components/split-layout'
import Stack from '../../../../commons/src/components/stack'
import ActionHeaderLayout from '../../components/action-header-layout'
import Container from '../../components/container'
import Table from '../../components/table'
import TableCell from '../../components/table/TableCell'
import TableHeaderCell from '../../components/table/TableHeaderCell'
import TableRow from '../../components/table/TableRow'

import {
  CreditNoteRowContent,
  InvoiceRowContent,
  OrderCreditNote,
  OrderDetailTab,
  OrderInvoice,
} from '../../order-types'
import ROUTES from '../../routes'
import DateRangePartial from '../date-range-partial'
import BooleanMatchingFilterPartial from '../filters/boolean-matching-filter-partial'

class LoadInvoicesError extends Error {
  constructor(message?: string) {
    super(message || `Loading invoices failed`)
    this.name = 'LoadInvoicesError'
  }
}

const tableColumnWidths = {
  orderNumber: '115px',
  invoiceDate: 1,
  placementDate: 1,
  production: 1,
  additionalCosts: 1,
  margin: 1,
  productionMinusMargin: 1,
  additionalCostsMinusMargin: 1,
  productionPremiumShare: 1,
  shipping: 1,
  totalToInvoice: 1.5,
}

const SORT_ATTRIBUTES: SortDefinition<InvoiceRowContent> = {
  placementDate: {
    asc: ({placementDate: a}, {placementDate: b}) =>
      typeof a !== 'undefined' && typeof b !== 'undefined' ? (isBefore(a, b) ? -1 : 1) : -1,
    desc: ({placementDate: a}, {placementDate: b}) =>
      typeof a !== 'undefined' && typeof b !== 'undefined' ? (isBefore(a, b) ? 1 : -1) : -1,
  },
  invoiceDate: {
    asc: ({invoiceDate: a}, {invoiceDate: b}) =>
      typeof a !== 'undefined' && typeof b !== 'undefined' ? (isBefore(a, b) ? -1 : 1) : -1,
    desc: ({invoiceDate: a}, {invoiceDate: b}) =>
      typeof a !== 'undefined' && typeof b !== 'undefined' ? (isBefore(a, b) ? 1 : -1) : -1,
  },
}

const CREDIT_NOTES_SORT_ATTRIBUTES: SortDefinition<CreditNoteRowContent> = {
  placementDate: {
    asc: ({placementDate: a}, {placementDate: b}) =>
      typeof a !== 'undefined' && typeof b !== 'undefined' ? (isBefore(a, b) ? -1 : 1) : -1,
    desc: ({placementDate: a}, {placementDate: b}) =>
      typeof a !== 'undefined' && typeof b !== 'undefined' ? (isBefore(a, b) ? 1 : -1) : -1,
  },
  creationDate: {
    asc: ({creationDate: a}, {creationDate: b}) =>
      typeof a !== 'undefined' && typeof b !== 'undefined' ? (isBefore(a, b) ? -1 : 1) : -1,
    desc: ({creationDate: a}, {creationDate: b}) =>
      typeof a !== 'undefined' && typeof b !== 'undefined' ? (isBefore(a, b) ? 1 : -1) : -1,
  },
}

const invoicesDateRangeConfig: DateRangeConfig = config.dateRangeConfig.invoices

const initialInvoicesDateRange = {
  from: getFirstDayOfCurrentMonth(),
  to: getTodaysDate(),
}

const initialFilters: FilterMap<InvoiceRowContent | CreditNoteRowContent> = {
  additionalCost: new BooleanMatchingFilter({
    key: 'hasAdditionalCosts',
    filterValue: undefined,
  }),
}

const statusFilterOptions = {
  undefined: 'Show all',
  true: 'Show only invoices with additional costs',
  false: 'Hide invoices with additional costs',
}

const InvoicesPartial = () => {
  const isAdminMode = useAppSelector(state => state.auth.isAdminMode)
  const provider = useAppSelector(state => state.core.provider)
  invariant(provider, 'Provider is missing.')

  const [invoicesDateRange, setInvoicesDateRange] =
    React.useState<DateRange>(initialInvoicesDateRange)
  const [invoices, setInvoices] = React.useState<OrderInvoice[]>([])
  const [creditNotes, setCreditNotes] = React.useState<OrderCreditNote[]>([])
  const [invoicesError, setInvoicesError] = React.useState('')
  const [isLoadingInvoices, setIsLoadingInvoices] = React.useState(false)

  const dispatch = useAppDispatch()

  invariant(provider, 'Provider is missing.')

  const {filters, setFilter} = useTableRowFilters<InvoiceRowContent | CreditNoteRowContent>(
    initialFilters
  )

  const tableRows = React.useMemo(() => {
    if (isLoadingInvoices || isEmpty(invoices)) {
      return []
    }

    return invoices.map(invoice => ({
      orderNumber: invoice.orderNumber,
      orderId: invoice.orderId,
      placementDate: invoice.orderDate,
      invoiceDate: invoice.created,
      currency: invoice.providerInvoiceables.currency,
      production: invoice.amounts.vendorProductionPrice,
      additionalCosts: invoice.amounts.additionalCostsTotal || 0,
      margin: invoice.providerInvoiceables.margin,
      productionMinusMargin: invoice.providerInvoiceables.productionMinusMargin,
      additionalCostsMinusMargin: invoice.providerInvoiceables.additionalCostsMinusMargin,
      productionPremiumShare: invoice.providerInvoiceables.productionPremiumShare,
      shipping: invoice.providerInvoiceables.shipping,
      totalToInvoice: invoice.providerInvoiceables.totalToInvoice,
      hasAdditionalCosts: !!invoice.amounts.additionalCostsTotal,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices])

  const originalCreditNoteTableRows = React.useMemo(() => {
    if (isLoadingInvoices || isEmpty(creditNotes)) {
      return []
    }

    return creditNotes.map(creditNote => ({
      orderNumber: creditNote.orderNumber,
      orderId: creditNote.orderId,
      placementDate: creditNote.orderDate,
      creationDate: creditNote.created,
      currency: creditNote.providerInvoiceables.currency,
      production: creditNote.amounts.vendorProductionPrice,
      additionalCosts: creditNote.amounts.additionalCostsTotal || 0,
      margin: creditNote.providerInvoiceables.margin,
      productionMinusMargin: creditNote.providerInvoiceables.productionMinusMargin,
      additionalCostsMinusMargin: creditNote.providerInvoiceables.additionalCostsMinusMargin,
      productionPremiumShare: creditNote.providerInvoiceables.productionPremiumShare,
      shipping: creditNote.providerInvoiceables.shipping,
      totalToInvoice: creditNote.providerInvoiceables.totalToInvoice,
      hasAdditionalCosts: !!creditNote.amounts.additionalCostsTotal,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditNotes])

  const searchableList = React.useMemo(
    () => [...tableRows, ...originalCreditNoteTableRows],
    [tableRows, originalCreditNoteTableRows]
  )

  const {searchResults, setSearchValue, searchValue} = useSearch(
    searchableList,
    createInvoiceSearch
  )

  const filteredTableRows = React.useMemo(
    () => filterTableRows(tableRows, Object.values(filters)),
    [filters, tableRows]
  )

  const filteredCreditNoteTableRows = React.useMemo(
    () => filterTableRows(originalCreditNoteTableRows, Object.values(filters)),
    [filters, originalCreditNoteTableRows]
  )

  const getTotalInvoices = (items: InvoiceRowContent[]) =>
    items.reduce((sum, item) => sum + item.totalToInvoice, 0)

  const getTotalCreditNotes = (items: CreditNoteRowContent[]) =>
    items.reduce((sum, item) => sum + item.totalToInvoice, 0)

  const [invoiceTableRows, invoicesTotal, invoicesTotalAmountRow] = React.useMemo(() => {
    const rows = intersection(searchResults, filteredTableRows) as InvoiceRowContent[]

    const totalAmountRow = {
      orderNumber: '',
      orderId: '',
      placementDate: undefined,
      invoiceDate: undefined,
      currency: rows[0]?.currency || '',
      production: 0,
      additionalCosts: 0,
      margin: 0,
      productionMinusMargin: sumBy(rows, r => r.productionMinusMargin),
      additionalCostsMinusMargin: sumBy(rows, r => r.additionalCostsMinusMargin),
      productionPremiumShare: sumBy(rows, r => r.productionPremiumShare),
      shipping: sumBy(rows, r => r.shipping),
      totalToInvoice: sumBy(rows, r => r.totalToInvoice),
      hasAdditionalCosts: false,
    }
    return [rows, getTotalInvoices(rows), totalAmountRow]
  }, [searchResults, filteredTableRows])

  const [creditNoteTableRows, creditNotesTotal, creditNotesTotalAmountRow] = React.useMemo(() => {
    const rows = intersection(searchResults, filteredCreditNoteTableRows) as CreditNoteRowContent[]
    const totalAmountRow = {
      orderNumber: '',
      orderId: '',
      placementDate: undefined,
      creationDate: undefined,
      currency: rows[0]?.currency || '',
      production: 0,
      additionalCosts: 0,
      margin: 0,
      productionMinusMargin: sumBy(rows, r => r.productionMinusMargin),
      additionalCostsMinusMargin: sumBy(rows, r => r.additionalCostsMinusMargin),
      productionPremiumShare: sumBy(rows, r => r.productionPremiumShare),
      shipping: sumBy(rows, r => r.shipping),
      totalToInvoice: sumBy(rows, r => r.totalToInvoice),
      hasAdditionalCosts: false,
    }

    return [rows, getTotalCreditNotes(rows), totalAmountRow]
  }, [searchResults, filteredCreditNoteTableRows])

  const {
    sortedItems: sortedInvoiceTableRows,
    sortAttribute,
    sortOrder,
    toggleSortBy,
  } = useSortAttributes(invoiceTableRows, SORT_ATTRIBUTES, 'invoiceDate')

  const {
    sortedItems: sortedCreditNoteTableRows,
    sortAttribute: creditNoteSortAttribute,
    sortOrder: creditNoteSortOrder,
    toggleSortBy: creditNoteToggleSort,
  } = useSortAttributes(creditNoteTableRows, CREDIT_NOTES_SORT_ATTRIBUTES, 'creationDate')

  const loadInvoicesAndCreditNotes = async () => {
    setIsLoadingInvoices(true)
    try {
      const sanitizedDateRange = {
        from: getStartOfDay(invoicesDateRange.from),
        to: getEndOfDay(invoicesDateRange.to),
      }

      if (isAdminMode) {
        const [invoicesResponse, creditNoteResponse] = await Promise.all([
          internalApi.getInvoices(provider.id, sanitizedDateRange),
          internalApi.getCreditNotes(provider.id, sanitizedDateRange),
        ])
        const transformedInvoices = invoicesResponse.map(invoice =>
          transformInternalOrderInvoice(invoice)
        )
        const transformedCreditNotes = creditNoteResponse.map(creditNote =>
          transformInternalCreditNote(creditNote)
        )
        setInvoices(transformedInvoices)
        setCreditNotes(transformedCreditNotes)
      } else {
        const [invoicesResponse, creditNoteResponse] = await Promise.all([
          partnerApi.getInvoices(sanitizedDateRange),
          partnerApi.getCreditNotes(sanitizedDateRange),
        ])
        setInvoices(invoicesResponse)
        setCreditNotes(creditNoteResponse)
      }
      setInvoicesError('')
    } catch (error) {
      if (error && error instanceof HttpStatusError && error.response.status === 401) {
        captureIgnoredException(error)
        dispatch(signOut())
      } else {
        captureException(new LoadInvoicesError((error as Error)?.message), 'default', {
          originalError: error,
          dateFrom: invoicesDateRange.from,
          dateto: invoicesDateRange.to,
          providerId: provider?.id,
        })
        setInvoicesError(
          'We are currently unable to load any of your invoices. Please wait a few minutes and then try again.'
        )
      }
    } finally {
      setIsLoadingInvoices(false)
    }
  }

  const getTableEmpty = () => {
    if (invoicesError) {
      return <Paragraph error>{invoicesError}</Paragraph>
    }
    return <Paragraph minor>No invoices found for the specified time range or filters.</Paragraph>
  }

  const getCreditNotesTableEmpty = () => {
    if (invoicesError) {
      return <Paragraph error>{invoicesError}</Paragraph>
    }
    return (
      <Paragraph minor>No credit notes found for the specified time range or filters.</Paragraph>
    )
  }

  const renderInvoiceRow = (
    row: InvoiceRowContent,
    emphasized = false,
    totalPrefix = '',
    showZeroAsEmpty = false
  ) => (
    <TableRow key={row.orderNumber} emphasized={emphasized}>
      <TableCell strong>
        <Link
          href={`${ROUTES.ORDER_DETAIL.replace(':id', row.orderId)}/${OrderDetailTab.DETAILS}`}
          target="_blank"
        >
          {row.orderNumber}
        </Link>
      </TableCell>
      <TableCell align="right">
        <span>{row.placementDate ? formatDate(row.placementDate) : ''}</span>
      </TableCell>
      <TableCell align="right">
        <span>{row.invoiceDate ? formatDate(row.invoiceDate) : ''}</span>
      </TableCell>
      <TableCell align="right">
        <span>
          {showZeroAsEmpty && row.production === 0
            ? ''
            : formatPrice(row.production, row.currency, 2)}
        </span>
      </TableCell>
      <TableCell align="right">
        <span>
          {showZeroAsEmpty && row.additionalCosts === 0
            ? ''
            : formatPrice(row.additionalCosts, row.currency, 2)}
        </span>
      </TableCell>
      <TableCell align="right" minor>
        <span>{showZeroAsEmpty && row.margin === 0 ? '' : `${row.margin}%`}</span>
      </TableCell>
      <TableCell align="right">
        <span>{formatPrice(row.productionMinusMargin, row.currency, 2)}</span>
      </TableCell>
      <TableCell align="right">
        <span>{formatPrice(row.additionalCostsMinusMargin, row.currency, 2)}</span>
      </TableCell>
      <TableCell align="right">
        <span>{formatPrice(row.productionPremiumShare, row.currency, 2)}</span>
      </TableCell>
      <TableCell align="right">
        <span>{formatPrice(row.shipping, row.currency, 2)}</span>
      </TableCell>
      <TableCell align="right" strong>
        <span>
          {totalPrefix}
          {formatPrice(row.totalToInvoice, row.currency, 2)}
        </span>
      </TableCell>
    </TableRow>
  )

  const renderInvoicesTable = () => (
    <Table
      isLoading={isLoadingInvoices}
      empty={getTableEmpty()}
      header={
        <TableRow>
          <TableHeaderCell width={tableColumnWidths.orderNumber}>Order number</TableHeaderCell>
          <TableHeaderCell
            width={tableColumnWidths.placementDate}
            sort={sortAttribute === 'placementDate' && sortOrder ? sortOrder : undefined}
            onClick={() => toggleSortBy('placementDate')}
            align="right"
          >
            Placement date
          </TableHeaderCell>
          <TableHeaderCell
            width={tableColumnWidths.invoiceDate}
            sort={sortAttribute === 'invoiceDate' && sortOrder ? sortOrder : undefined}
            onClick={() => toggleSortBy('invoiceDate')}
            align="right"
          >
            Invoice date
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.production} align="right">
            Production
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.additionalCosts} align="right">
            Additional Costs
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.margin} align="right">
            Margin
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.productionMinusMargin} align="right">
            Production - Margin
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.additionalCostsMinusMargin} align="right">
            Additional Costs - Margin
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.productionPremiumShare} align="right">
            Production Premium Share
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.shipping} align="right">
            Shipping
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.totalToInvoice} align="right">
            Total
          </TableHeaderCell>
        </TableRow>
      }
    >
      {sortedInvoiceTableRows.map(tableRow => renderInvoiceRow(tableRow))}
      {sortedInvoiceTableRows.length > 0 &&
        renderInvoiceRow(invoicesTotalAmountRow, true, 'Total: ', true)}
    </Table>
  )

  const renderCreditNoteRow = (
    row: CreditNoteRowContent,
    emphasized = false,
    totalPrefix = '',
    showZeroAsEmpty = false
  ) => (
    <TableRow key={row.orderNumber} emphasized={emphasized}>
      <TableCell strong>
        <Link
          href={`${ROUTES.ORDER_DETAIL.replace(':id', row.orderId)}/${OrderDetailTab.DETAILS}`}
          target="_blank"
        >
          {row.orderNumber}
        </Link>
      </TableCell>
      <TableCell align="right">
        <span>{row.placementDate ? formatDate(row.placementDate) : ''}</span>
      </TableCell>
      <TableCell align="right">
        <span>{row.creationDate ? formatDate(row.creationDate) : ''}</span>
      </TableCell>
      <TableCell align="right">
        <span>
          {showZeroAsEmpty && row.production === 0
            ? ''
            : formatPrice(-row.production, row.currency, 2)}
        </span>
      </TableCell>
      <TableCell align="right">
        <span>
          {showZeroAsEmpty && row.additionalCosts === 0
            ? ''
            : formatPrice(-row.additionalCosts, row.currency, 2)}
        </span>
      </TableCell>
      <TableCell align="right" minor>
        <span>{showZeroAsEmpty && row.margin === 0 ? '' : `${row.margin}%`}</span>
      </TableCell>
      <TableCell align="right">
        <span>{formatPrice(-row.productionMinusMargin, row.currency, 2)}</span>
      </TableCell>
      <TableCell align="right">
        <span>{formatPrice(-row.additionalCostsMinusMargin, row.currency, 2)}</span>
      </TableCell>
      <TableCell align="right">
        <span>{formatPrice(-row.productionPremiumShare, row.currency, 2)}</span>
      </TableCell>
      <TableCell align="right">
        <span>{formatPrice(-row.shipping, row.currency, 2)}</span>
      </TableCell>
      <TableCell align="right" strong>
        <span>
          {totalPrefix}
          {formatPrice(-row.totalToInvoice, row.currency, 2)}
        </span>
      </TableCell>
    </TableRow>
  )

  const renderCreditNotesTable = () => (
    <Table
      isLoading={isLoadingInvoices}
      empty={getCreditNotesTableEmpty()}
      header={
        <TableRow>
          <TableHeaderCell width={tableColumnWidths.orderNumber}>Order number</TableHeaderCell>
          <TableHeaderCell
            width={tableColumnWidths.invoiceDate}
            sort={
              creditNoteSortAttribute === 'invoiceDate' && creditNoteSortOrder
                ? creditNoteSortOrder
                : undefined
            }
            onClick={() => creditNoteToggleSort('invoiceDate')}
            align="right"
          >
            Placement date
          </TableHeaderCell>
          <TableHeaderCell
            width={tableColumnWidths.placementDate}
            sort={
              creditNoteSortAttribute === 'placementDate' && creditNoteSortOrder
                ? creditNoteSortOrder
                : undefined
            }
            onClick={() => creditNoteToggleSort('placementDate')}
            align="right"
          >
            Refund date
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.production} align="right">
            Production
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.additionalCosts} align="right">
            Additional Costs
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.margin} align="right">
            Margin
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.productionMinusMargin} align="right">
            Production - Margin
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.additionalCostsMinusMargin} align="right">
            Additional Costs - Margin
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.productionPremiumShare} align="right">
            Production Premium Share
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.shipping} align="right">
            Shipping
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.totalToInvoice} align="right">
            Total
          </TableHeaderCell>
        </TableRow>
      }
    >
      {sortedCreditNoteTableRows.map(tableRow => renderCreditNoteRow(tableRow))}
      {sortedCreditNoteTableRows.length > 0 &&
        renderCreditNoteRow(creditNotesTotalAmountRow, true, 'Total: ', true)}
    </Table>
  )

  const totalTotal = invoicesTotal - creditNotesTotal

  return (
    <Container>
      <ActionHeaderLayout
        respectPageTabs
        filterBar={
          <Stack space="200">
            <SplitLayout
              verticalAlignment="end"
              left={
                <ActionBar noWrap alignItems="bottom">
                  <DateRangePartial
                    inputLabel="Invoice date"
                    dateRange={invoicesDateRange}
                    dateRangeConfig={invoicesDateRangeConfig}
                    onChange={setInvoicesDateRange}
                    disabled={isLoadingInvoices}
                  />
                  <Button
                    onClick={loadInvoicesAndCreditNotes}
                    icon={isLoadingInvoices ? <LoadingIndicator /> : <Icon source={refreshIcon} />}
                    iconAlign="right"
                    disabled={isLoadingInvoices}
                  >
                    Load
                  </Button>
                </ActionBar>
              }
              right={
                <Stack space="60">
                  <ActionBar>
                    <Headline label="Your Earnings:" className="u-no-margin-bottom" />
                    <Paragraph size="l" strong className="u-no-margin-bottom">
                      {totalTotal > 0 ? formatPrice(totalTotal, provider.currency, 2) : '-'}
                    </Paragraph>
                  </ActionBar>
                  <Paragraph size="xs" minor className="u-no-margin-bottom">
                    For the specified time range
                  </Paragraph>
                </Stack>
              }
            />
            <FilterBar
              filtersLabel="Filter invoices and credit notes by"
              filters={
                <BooleanMatchingFilterPartial
                  label="Additional costs"
                  size="90"
                  options={statusFilterOptions}
                  filterKey="additionalCost"
                  filterMap={filters}
                  tableRows={searchResults}
                  applyFilter={newFilter => setFilter('additionalCost', newFilter)}
                />
              }
              searchBar={
                <SearchInputField
                  size="90"
                  name="order-search"
                  placeholder="Search by order number"
                  value={searchValue}
                  onChange={value => {
                    setSearchValue(value)
                  }}
                />
              }
            />
          </Stack>
        }
      >
        <Stack space="400">
          <Stack>
            <Headline label="Invoices" className="u-no-margin-bottom" />
            {renderInvoicesTable()}
          </Stack>
          <Stack>
            <Headline label="Credit Notes" className="u-no-margin-bottom" />
            {renderCreditNotesTable()}
          </Stack>
        </Stack>
      </ActionHeaderLayout>
    </Container>
  )
}

export default InvoicesPartial
